// ============================================================================
// @torre/expenses — Gasto por categoría y tipo de costo (gráficas de Gastos)
// ============================================================================
// Lo que muestran las gráficas del registro de gastos, sobre los gastos YA
// filtrados que tiene la pantalla (en moneda base):
//
//   · cuánto se gastó por categoría en el periodo (pastel)
//   · en qué categoría se gastó mes a mes (barras apiladas)
//   · cuánto de eso es gasto FIJO y cuánto VARIABLE
//
// Fijo / variable: cada gasto puede llevar su etiqueta (`costType`). Sin ella
// se toma la de su categoría (`DEFAULT_FIXED_CATEGORIES`): lo que se paga cada
// mes pase lo que pase —nómina y suscripciones— es fijo; lo demás, variable.
//
// Lógica pura: el front la calcula con la lista que ya pidió al API.
// ============================================================================

import { expenseCostType, isCategoryId, round2, type Expense, type ExpenseCategory, type ExpenseCostType } from "./index";

type SpendRow = Pick<Expense, "date" | "category"> & { amountBase?: number | null; costType?: ExpenseCostType | null };

export interface CategorySpendSlice {
  category: ExpenseCategory;
  total: number;
  fixed: number;
  variable: number;
  count: number;
  /** Parte del gasto del periodo, 0 a 1, entre las categorías con gasto neto positivo. */
  share: number;
}

export interface CategorySpendMonth {
  /** `YYYY-MM`. */
  period: string;
  total: number;
  fixed: number;
  variable: number;
  byCategory: Partial<Record<ExpenseCategory, number>>;
  /** Parte FIJA de cada categoría (lo demás de `byCategory` es variable). */
  fixedByCategory?: Partial<Record<ExpenseCategory, number>>;
}

export interface CategorySpend {
  total: number;
  fixed: number;
  variable: number;
  /** Parte fija del gasto, 0 a 1 (sobre lo fijo más lo variable, sin devoluciones netas). */
  fixedShare: number;
  count: number;
  /** Gastos en otra moneda sin tipo de cambio: no suman a ninguna gráfica. */
  withoutRate: number;
  /** Categorías con movimientos, de mayor a menor gasto. */
  categories: CategorySpendSlice[];
  /** Un renglón por mes, del primero al último con gasto, sin huecos (ver `MAX_GAP_FILL_MONTHS`). */
  months: CategorySpendMonth[];
}

/**
 * Una categoría que no está en el catálogo (dato roto) cae en «otros». El
 * catálogo es el de la compañía (#1255): `categories` si se pasa (el back), o el
 * activo (el front), e incluye las archivadas, que siguen saliendo con su nombre.
 */
const categoryOf = (value: unknown, categories?: readonly string[] | null): ExpenseCategory =>
  isCategoryId(value, categories) ? (value as ExpenseCategory) : "otros";

/** Catálogo de la compañía para quien no usa el activo (el back). */
export interface CategorySpendOptions {
  /** Ids de categoría válidos (incluidas las archivadas). */
  categories?: readonly string[] | null;
  /** Ids cuyos gastos son fijos por omisión. */
  fixedCategories?: readonly string[] | null;
}

/** `YYYY-MM` con un mes real: una fecha rota no puede alargar el eje del tiempo. */
const PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * Meses que se rellenan entre el primero y el último con gasto. Con un rango
 * mayor (una fecha mal capturada, p. ej. el año 2062) solo salen los meses con
 * movimientos, en vez de cientos de barras vacías.
 */
export const MAX_GAP_FILL_MONTHS = 120;

const monthIndex = (period: string) => Number(period.slice(0, 4)) * 12 + Number(period.slice(5, 7));

function nextPeriod(period: string): string {
  const [y, m] = period.split("-").map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, "0")}`;
}

/**
 * Gasto por categoría, por mes y por tipo de costo de una lista de gastos.
 * Las devoluciones (monto negativo) restan en su categoría y en su mes.
 */
export function categorySpend(rows: readonly SpendRow[], options: CategorySpendOptions = {}): CategorySpend {
  const byCategory = new Map<ExpenseCategory, { total: number; fixed: number; variable: number; count: number }>();
  const byMonth = new Map<string, CategorySpendMonth>();
  let total = 0;
  let fixed = 0;
  let variable = 0;
  let count = 0;
  let withoutRate = 0;

  for (const row of rows) {
    if (row.amountBase == null || !Number.isFinite(row.amountBase)) {
      withoutRate += 1;
      continue;
    }
    const amount = row.amountBase;
    const category = categoryOf(row.category, options.categories);
    const isFixed = expenseCostType({ category, costType: row.costType }, options.fixedCategories) === "fijo";
    const period = String(row.date || "").slice(0, 7);

    const slice = byCategory.get(category) || { total: 0, fixed: 0, variable: 0, count: 0 };
    slice.total += amount;
    slice[isFixed ? "fixed" : "variable"] += amount;
    slice.count += 1;
    byCategory.set(category, slice);

    if (PERIOD_RE.test(period)) {
      const month = byMonth.get(period) || { period, total: 0, fixed: 0, variable: 0, byCategory: {}, fixedByCategory: {} };
      month.total += amount;
      month[isFixed ? "fixed" : "variable"] += amount;
      month.byCategory[category] = (month.byCategory[category] || 0) + amount;
      if (isFixed) month.fixedByCategory![category] = (month.fixedByCategory![category] || 0) + amount;
      byMonth.set(period, month);
    }

    total += amount;
    if (isFixed) fixed += amount;
    else variable += amount;
    count += 1;
  }

  // Las partes se miden contra el gasto positivo: una categoría con más
  // devoluciones que gasto no infla a las demás por encima del 100 %.
  let positive = 0;
  for (const s of byCategory.values()) positive += Math.max(0, s.total);
  const categories = [...byCategory.entries()]
    .map(([category, s]) => ({
      category,
      total: round2(s.total),
      fixed: round2(s.fixed),
      variable: round2(s.variable),
      count: s.count,
      share: positive > 0 ? Math.max(0, s.total) / positive : 0,
    }))
    .sort((a, b) => b.total - a.total || a.category.localeCompare(b.category));

  const months: CategorySpendMonth[] = [];
  const periods = [...byMonth.keys()].sort();
  if (periods.length) {
    const last = periods[periods.length - 1];
    const fill = monthIndex(last) - monthIndex(periods[0]) < MAX_GAP_FILL_MONTHS;
    const shown: string[] = [];
    if (fill) for (let p = periods[0]; p <= last; p = nextPeriod(p)) shown.push(p);
    else shown.push(...periods);
    for (const p of shown) {
      const m = byMonth.get(p) || { period: p, total: 0, fixed: 0, variable: 0, byCategory: {}, fixedByCategory: {} };
      const round = (values: Partial<Record<ExpenseCategory, number>> = {}) => {
        const out: Partial<Record<ExpenseCategory, number>> = {};
        for (const [k, v] of Object.entries(values)) out[k as ExpenseCategory] = round2(v as number);
        return out;
      };
      months.push({ period: p, total: round2(m.total), fixed: round2(m.fixed), variable: round2(m.variable), byCategory: round(m.byCategory), fixedByCategory: round(m.fixedByCategory) });
    }
  }

  const costBase = Math.max(0, fixed) + Math.max(0, variable);
  const fixedShare = costBase > 0 ? Math.max(0, fixed) / costBase : 0;
  return { total: round2(total), fixed: round2(fixed), variable: round2(variable), fixedShare, count, withoutRate, categories, months };
}
